"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type Attribution,
  type AttributionInput,
  type AttributionNote,
  buildAttribution,
  isPublicDomain,
  isReady,
  LICENCE_CODES,
  type LicenceCode,
  licenceName,
  type Phrases,
  type Segment,
  segmentsToHtml,
  segmentsToPlain,
  type Version,
  versionsFor,
} from "@/lib/tool-kit/legal-styles/creative-commons";

/** English strings. A translated content file overrides these through `ui`. */
export const CREATIVE_COMMONS_ATTRIBUTION_GENERATOR_STRINGS = {
  formHeading: "The work you are crediting",
  resultsHeading: "Your attribution",
  emptyPrompt:
    "Enter the work's title, or its author or source, to see your attribution.",
  tryExample: "Try an example",
  clear: "Clear",
  copy: "Copy",
  copied: "Copied!",
  titleLabel: "Title of the work",
  titlePlaceholder: "Creative Commons 10th Birthday Celebration San Francisco",
  titleHint:
    "Required by licence versions 3.0 and earlier if the work has a title; optional in 4.0. Leave it blank for an untitled work.",
  sourceLabel: "Source: the page where the work is published",
  sourcePlaceholder: "https://www.flickr.com/photos/…",
  sourceHint:
    "Link to where the work was first shared publicly, not a shortened link or a search result.",
  authorLabel: "Author",
  authorPlaceholder: "Timothy Vollmer",
  authorHint:
    "The name the licensor asks for, including a pseudonym or an organisation. Crediting a site such as Wikimedia Commons does not replace crediting the author.",
  authorUrlLabel: "Author's page (optional)",
  authorUrlPlaceholder: "https://www.flickr.com/photos/sixteenmilesofstring/",
  anonymousLabel:
    "The author is anonymous or asked not to be named: leave the name out",
  noticeLabel: "Copyright notice (optional)",
  noticePlaceholder: "© 2019–2022 Kyle Simpson",
  noticeHint: "Include it exactly as the author gives it.",
  institutionLabel: "Museum, library or archive that holds it (public domain)",
  institutionPlaceholder: "The Rijksmuseum collection",
  licenceLabel: "Licence",
  versionLabel: "Version",
  versionHint:
    "Use the version the author chose. Creative Commons recommends 4.0 for your own work.",
  licences: {
    by: "CC BY — Attribution",
    "by-sa": "CC BY-SA — Attribution-ShareAlike",
    "by-nc": "CC BY-NC — Attribution-NonCommercial",
    "by-nc-sa": "CC BY-NC-SA — Attribution-NonCommercial-ShareAlike",
    "by-nd": "CC BY-ND — Attribution-NoDerivatives",
    "by-nc-nd": "CC BY-NC-ND — Attribution-NonCommercial-NoDerivatives",
    cc0: "CC0 — Public Domain Dedication",
    pdm: "Public Domain Mark",
  },
  modifiedLabel: "I changed the work",
  changeLabel: "What you changed",
  changePlaceholder: "Cropped from original",
  changeHint:
    "A short note such as Cropped from original or Translated into French.",
  adaptationTitleLabel: "Title of your adaptation (optional)",
  adaptationTitlePlaceholder: "90fied",
  adaptationTitleHint:
    "If you made a new work from this one, give it a title to get the adaptation credit.",
  adapterLabel: "Your name (for the adaptation)",
  adapterPlaceholder: "Alex Rivera",
  webHeading: "On a web page, slide or document (with links)",
  htmlHeading: "HTML",
  printHeading: "In print or a PDF (full URLs)",
  captionHeading: "Short credit for a caption",
  copyRich: "Copy with links",
  copyHtml: "Copy HTML",
  copyText: "Copy text",
  licenceHeading: "The licence",
  deedLabel: "Licence summary (deed)",
  legalCodeLabel: "Full licence text",
  phrases: {
    licensed: "{work} by {author} is licensed under {licence}.",
    licensedNoAuthor: "{work} is licensed under {licence}.",
    modified: "{work} by {author}, used under {licence} / {change}",
    modifiedNoAuthor: "{work}, used under {licence} / {change}",
    adaptation:
      "This work, {new}, is adapted from {work} by {author}, used under {licence}. {new} is licensed under {licence2} by {adapter}.",
    adaptationNoAuthor:
      "This work, {new}, is adapted from {work}, used under {licence}. {new} is licensed under {licence2} by {adapter}.",
    publicDomain: "{work} by {author}. ",
    publicDomainNoAuthor: "{work}. ",
    dedicated: "Dedicated to the public domain under {licence}.",
    marked: "Marked as being in the public domain ({licence}).",
    caption: "{work} by {author} / {licence}",
    captionNoAuthor: "{work} / {licence}",
    untitled: "This work",
    quoteOpen: "“",
    quoteClose: "”",
    printUrl: "{text} ({url})",
  },
  notes: {
    noDerivatives:
      "ND licences do not allow adaptations. A technical change needed to use the work in another format is fine under 4.0, but anything that changes its meaning needs the author's permission.",
    shareAlike:
      "ShareAlike: if you share your changed version, you must licence it under the same licence or a compatible one.",
    nonCommercial:
      "NonCommercial: you may use the work only for purposes that are not primarily for commercial advantage or monetary compensation.",
    titleRequired:
      "Versions 3.0 and earlier ask you to give the title of the work if it has one.",
    olderVersion:
      "This is an older licence version. It is still valid for work already licensed under it, so credit it as the author did.",
    publicDomain:
      "A work in the public domain needs no credit by law. Creative Commons still recommends naming the source and the institution that holds it, which helps readers find it and supports academic integrity.",
    anonymous:
      "The author's name is left out, but the title, source and licence stay in.",
    removeOnRequest:
      "If the author asks you to remove their name or details, the 4.0 licences require you to remove them as far as reasonably practicable.",
  },
  exampleOriginal: {
    title: "Creative Commons 10th Birthday Celebration San Francisco",
    sourceUrl:
      "https://www.flickr.com/photos/sixteenmilesofstring/8256206923/in/set-72157632200936657",
    author: "Timothy Vollmer",
    authorUrl: "https://www.flickr.com/photos/sixteenmilesofstring/",
    licence: "by",
    version: "4.0",
  },
  exampleCropped: {
    title: "Creative Commons 10th Birthday Celebration San Francisco",
    sourceUrl:
      "https://www.flickr.com/photos/sixteenmilesofstring/8256206923/in/set-72157632200936657",
    author: "Timothy Vollmer",
    authorUrl: "https://www.flickr.com/photos/sixteenmilesofstring/",
    licence: "by",
    version: "4.0",
    modified: "true",
    change: "Cropped from original",
  },
  exampleAdaptation: {
    title: "Creative Commons 10th Birthday Celebration San Francisco",
    sourceUrl:
      "https://www.flickr.com/photos/sixteenmilesofstring/8256206923/in/set-72157632200936657",
    author: "Timothy Vollmer",
    authorUrl: "https://www.flickr.com/photos/sixteenmilesofstring/",
    licence: "by",
    version: "4.0",
    modified: "true",
    adaptationTitle: "90fied",
    adapter: "Alex Rivera",
  },
  exampleBook: {
    title: "You Don't Know JS Yet (2nd Edition)",
    sourceUrl: "https://github.com/getify/You-Dont-Know-JS",
    author: "Kyle Simpson",
    authorUrl: "https://github.com/getify",
    licence: "by-nc-nd",
    version: "4.0",
    notice: "© 2019-2022 Kyle Simpson",
  },
  examplePublicDomain: {
    title: "The Milkmaid",
    sourceUrl: "http://hdl.handle.net/10934/RM0001.COLLECT.6417",
    author: "Vermeer",
    institution: "The Rijksmuseum collection",
    licence: "cc0",
    version: "1.0",
  },
} as const;

type Strings = typeof CREATIVE_COMMONS_ATTRIBUTION_GENERATOR_STRINGS;

type Values = Record<string, string>;

export function inputFrom(values: Values): AttributionInput {
  const licence = (
    LICENCE_CODES.includes(values.licence as LicenceCode)
      ? values.licence
      : "by"
  ) as LicenceCode;
  const allowed = versionsFor(licence);
  const requested = (values.version || "4.0") as Version;
  const version = allowed.includes(requested) ? requested : allowed[0];
  return {
    title: (values.title ?? "").trim(),
    sourceUrl: (values.sourceUrl ?? "").trim(),
    author: (values.author ?? "").trim(),
    authorUrl: (values.authorUrl ?? "").trim(),
    notice: (values.notice ?? "").trim(),
    institution: (values.institution ?? "").trim(),
    licence,
    version,
    anonymous: values.anonymous === "true",
    modified: values.modified === "true",
    change: (values.change ?? "").trim(),
    adaptationTitle: (values.adaptationTitle ?? "").trim(),
    adapter: (values.adapter ?? "").trim(),
  };
}

function Linked({ segments }: { segments: Segment[] }) {
  return (
    <>
      {segments.map((segment, index) =>
        segment.href ? (
          <a
            className="text-primary underline"
            href={segment.href}
            // biome-ignore lint/suspicious/noArrayIndexKey: positional fragments of one sentence
            key={index}
            rel="noopener noreferrer"
            target="_blank"
          >
            {segment.text}
          </a>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: positional fragments of one sentence
          <span key={index}>{segment.text}</span>
        )
      )}
    </>
  );
}

function ResultBlock({
  heading,
  children,
  actions,
}: {
  heading: string;
  children: React.ReactNode;
  actions: React.ReactNode;
}) {
  return (
    <section className={`${CARD} space-y-3`}>
      <h3 className={WIDGET_H3}>{heading}</h3>
      <div className="break-words text-foreground leading-relaxed">
        {children}
      </div>
      <div className="flex flex-wrap gap-2">{actions}</div>
    </section>
  );
}

function Results({
  attribution,
  strings,
  noteKeys,
}: {
  attribution: Attribution;
  strings: Strings;
  noteKeys: AttributionNote[];
}) {
  const caption = segmentsToPlain(attribution.caption);
  return (
    <div className="space-y-4">
      <ResultBlock
        actions={
          <CopyButton
            copiedLabel={strings.copied}
            html={attribution.html}
            label={strings.copyRich}
            small
            text={attribution.plain}
          />
        }
        heading={strings.webHeading}
      >
        <Linked segments={attribution.web} />
      </ResultBlock>
      <ResultBlock
        actions={
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyHtml}
            small
            text={attribution.html}
          />
        }
        heading={strings.htmlHeading}
      >
        <code className="break-all font-mono text-sm">{attribution.html}</code>
      </ResultBlock>
      <ResultBlock
        actions={
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyText}
            small
            text={attribution.print}
          />
        }
        heading={strings.printHeading}
      >
        {attribution.print}
      </ResultBlock>
      <ResultBlock
        actions={
          <CopyButton
            copiedLabel={strings.copied}
            html={segmentsToHtml(attribution.caption)}
            label={strings.copyRich}
            small
            text={caption}
          />
        }
        heading={strings.captionHeading}
      >
        <Linked segments={attribution.caption} />
      </ResultBlock>
      <section className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.licenceHeading}</h3>
        <p className="text-foreground">{attribution.licenceName}</p>
        <p className={HINT}>
          {strings.deedLabel}: {attribution.deedUrl}
        </p>
        {attribution.legalCodeUrl ? (
          <p className={HINT}>
            {strings.legalCodeLabel}: {attribution.legalCodeUrl}
          </p>
        ) : null}
      </section>
      {noteKeys.length > 0 ? (
        <ul className="list-disc space-y-1 pl-5 text-muted-foreground text-sm">
          {noteKeys.map((key) => (
            <li key={key}>{strings.notes[key]}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default function CreativeCommonsAttributionGeneratorWidget() {
  const strings = useToolUi(CREATIVE_COMMONS_ATTRIBUTION_GENERATOR_STRINGS);
  const [values, setValues] = useState<Values>({
    licence: "by",
    version: "4.0",
  });
  const input = useMemo(() => inputFrom(values), [values]);
  const phrases: Phrases = strings.phrases;
  const attribution = useMemo(
    () => (isReady(input) ? buildAttribution(input, phrases) : null),
    [input, phrases]
  );
  const update = (key: string, value: string) =>
    setValues((current) => ({ ...current, [key]: value }));
  const versions = versionsFor(input.licence);
  const publicDomain = isPublicDomain(input.licence);

  return (
    <div className="space-y-6">
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className={WIDGET_H2}>{strings.formHeading}</h2>
          <div className="flex flex-wrap gap-2">
            <button
              className={BUTTON_SECONDARY}
              onClick={() => setValues({ ...strings.exampleOriginal })}
              type="button"
            >
              {strings.tryExample}
            </button>
            <button
              className={BUTTON_SECONDARY}
              onClick={() => setValues({ licence: "by", version: "4.0" })}
              type="button"
            >
              {strings.clear}
            </button>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <TextField
              hint={strings.titleHint}
              id="cc-title"
              label={strings.titleLabel}
              onChange={(value) => update("title", value)}
              placeholder={strings.titlePlaceholder}
              value={values.title ?? ""}
            />
          </div>
          <div className="sm:col-span-2">
            <TextField
              hint={strings.sourceHint}
              id="cc-source"
              label={strings.sourceLabel}
              onChange={(value) => update("sourceUrl", value)}
              placeholder={strings.sourcePlaceholder}
              type="url"
              value={values.sourceUrl ?? ""}
            />
          </div>
          <TextField
            hint={strings.authorHint}
            id="cc-author"
            label={strings.authorLabel}
            onChange={(value) => update("author", value)}
            placeholder={strings.authorPlaceholder}
            value={values.author ?? ""}
          />
          <TextField
            id="cc-author-url"
            label={strings.authorUrlLabel}
            onChange={(value) => update("authorUrl", value)}
            placeholder={strings.authorUrlPlaceholder}
            type="url"
            value={values.authorUrl ?? ""}
          />
          <div className="sm:col-span-2">
            <Checkbox
              checked={values.anonymous === "true"}
              label={strings.anonymousLabel}
              onChange={(checked) => update("anonymous", checked ? "true" : "")}
            />
          </div>
          <SelectField
            id="cc-licence"
            label={strings.licenceLabel}
            onChange={(value) => update("licence", value)}
            options={LICENCE_CODES.map((code) => ({
              value: code,
              label: strings.licences[code],
            }))}
            value={input.licence}
          />
          <SelectField
            hint={strings.versionHint}
            id="cc-version"
            label={strings.versionLabel}
            onChange={(value) => update("version", value)}
            options={versions.map((version) => ({
              value: version,
              label: licenceName(input.licence, version),
            }))}
            value={input.version}
          />
          <TextField
            hint={strings.noticeHint}
            id="cc-notice"
            label={strings.noticeLabel}
            onChange={(value) => update("notice", value)}
            placeholder={strings.noticePlaceholder}
            value={values.notice ?? ""}
          />
          {publicDomain ? (
            <TextField
              id="cc-institution"
              label={strings.institutionLabel}
              onChange={(value) => update("institution", value)}
              placeholder={strings.institutionPlaceholder}
              value={values.institution ?? ""}
            />
          ) : null}
        </div>
        {publicDomain ? null : (
          <div className="space-y-4">
            <Checkbox
              checked={input.modified}
              label={strings.modifiedLabel}
              onChange={(checked) => update("modified", checked ? "true" : "")}
            />
            {input.modified ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  hint={strings.changeHint}
                  id="cc-change"
                  label={strings.changeLabel}
                  onChange={(value) => update("change", value)}
                  placeholder={strings.changePlaceholder}
                  value={values.change ?? ""}
                />
                <TextField
                  hint={strings.adaptationTitleHint}
                  id="cc-adaptation"
                  label={strings.adaptationTitleLabel}
                  onChange={(value) => update("adaptationTitle", value)}
                  placeholder={strings.adaptationTitlePlaceholder}
                  value={values.adaptationTitle ?? ""}
                />
                <TextField
                  id="cc-adapter"
                  label={strings.adapterLabel}
                  onChange={(value) => update("adapter", value)}
                  placeholder={strings.adapterPlaceholder}
                  value={values.adapter ?? ""}
                />
              </div>
            ) : null}
          </div>
        )}
      </section>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {attribution ? (
          <Results
            attribution={attribution}
            noteKeys={attribution.notes}
            strings={strings}
          />
        ) : (
          <Notice>{strings.emptyPrompt}</Notice>
        )}
      </section>
    </div>
  );
}
